/**
 * Sprint 9 — Admin · Cleanup
 *
 * Super-admin-only panel listing every org and deployment, including
 * soft-deleted ones, with delete / restore controls. Backed by:
 *   GET    /api/admin/cleanup
 *   DELETE /api/admin/deployments/:id
 *   POST   /api/admin/deployments/:id/restore
 *   DELETE /api/admin/organizations/:id     (refuses if active deployments)
 *   POST   /api/admin/organizations/:id/restore
 *
 * Non-super-admin sessions get a friendly 403 banner and a return link.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { api, formatApiError } from "../lib/api";

export default function AdminCleanup() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState({ organizations: [], deployments: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showDeleted, setShowDeleted] = useState(true);
  const [busyKey, setBusyKey] = useState(null); // e.g. "dep:abc" or "org:xyz"
  const [confirming, setConfirming] = useState(null); // {kind, id, name, action}
  const [seedBusy, setSeedBusy] = useState(false);
  const [seedResult, setSeedResult] = useState(null); // {org_id, deployment_id, ...}
  const [priSeedBusy, setPriSeedBusy] = useState(false);
  const [priSeedResult, setPriSeedResult] = useState(null); // {seeded: [...], skipped_existing: [...]}
  const [priResetBusy, setPriResetBusy] = useState(false);
  const [priResetResult, setPriResetResult] = useState(null); // {reset_deployments: [...], removed: {...}}
  const [priResetConfirming, setPriResetConfirming] = useState(false);
  // User provisioning state
  const [users, setUsers] = useState([]);
  const [userForm, setUserForm] = useState({ email: "", name: "", role: "consultant" });
  const [userBusy, setUserBusy] = useState(false);
  const [newUserResult, setNewUserResult] = useState(null); // {user, password}
  const [pwdCopied, setPwdCopied] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const { data: body } = await api.get("/api/admin/cleanup");
      setData(body);
      // Users list — fetched alongside cleanup data since the panel
      // surfaces both.
      try {
        const { data: u } = await api.get("/api/admin/users");
        setUsers(u.users || []);
      } catch (_) {
        // non-fatal — users listing is a separate concern
      }
      setError("");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Async IIFE keeps setState off the effect's synchronous body
    // (react-hooks/set-state-in-effect).
    (async () => {
      if (user && user.role === "super_admin") {
        await refresh();
      } else if (user) {
        setLoading(false);
      }
    })();
  }, [user, refresh]);

  // ---------- derived state (must run before any early return so the
  // hook-call order stays stable across renders).
  const activeDepsByOrg = useMemo(() => {
    const m = {};
    for (const d of data.deployments) {
      if (d.deleted_at) continue;
      m[d.org_id] = (m[d.org_id] || 0) + 1;
    }
    return m;
  }, [data.deployments]);

  // ---------- 403 surface for non-super-admins
  if (user && user.role !== "super_admin") {
    return (
      <div className="min-h-screen bg-paper" data-testid="admin-cleanup-forbidden">
        <main id="main-content" className="max-w-2xl mx-auto px-8 py-24">
          <div className="eyebrow mb-3">Admin · Cleanup</div>
          <h1 className="mb-3" style={{ fontSize: "2.25rem" }}>This page is super-admin only.</h1>
          <p className="lead">
            The cleanup panel holds destructive actions (soft-delete orgs
            and deployments). Your current role doesn&rsquo;t have access.
          </p>
          <div className="mt-6">
            <Link to="/dashboard" className="btn-quiet" data-testid="cleanup-back-link">
              Back to home
            </Link>
          </div>
        </main>
      </div>
    );
  }

  // ---------- delete / restore handlers
  async function doDeploymentDelete(dep) {
    setBusyKey(`dep:${dep.id}`);
    try {
      await api.delete(`/api/admin/deployments/${dep.id}`);
      await refresh();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusyKey(null);
      setConfirming(null);
    }
  }

  async function doDeploymentRestore(dep) {
    setBusyKey(`dep:${dep.id}`);
    try {
      await api.post(`/api/admin/deployments/${dep.id}/restore`);
      await refresh();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusyKey(null);
      setConfirming(null);
    }
  }

  async function doOrgDelete(org) {
    setBusyKey(`org:${org.id}`);
    try {
      await api.delete(`/api/admin/organizations/${org.id}`);
      await refresh();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusyKey(null);
      setConfirming(null);
    }
  }

  async function doOrgRestore(org) {
    setBusyKey(`org:${org.id}`);
    try {
      await api.post(`/api/admin/organizations/${org.id}/restore`);
      await refresh();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusyKey(null);
      setConfirming(null);
    }
  }

  async function doSeedDemo() {
    setSeedBusy(true);
    setError("");
    setSeedResult(null);
    try {
      const { data: body } = await api.post("/api/admin/seed-demo-org");
      setSeedResult(body);
      await refresh();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSeedBusy(false);
    }
  }

  async function doSeedPriDemo() {
    setPriSeedBusy(true);
    setError("");
    setPriSeedResult(null);
    try {
      const { data: body } = await api.post("/api/admin/seed-pri-demo");
      setPriSeedResult(body);
      await refresh();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setPriSeedBusy(false);
    }
  }

  async function doResetPriDemo() {
    setPriResetBusy(true);
    setError("");
    setPriResetResult(null);
    setPriSeedResult(null);
    try {
      const { data: body } = await api.post("/api/admin/seed-pri-demo/reset");
      setPriResetResult(body);
      setPriResetConfirming(false);
      await refresh();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setPriResetBusy(false);
    }
  }

  async function doProvisionUser(e) {
    e.preventDefault();
    if (userBusy) return;
    setUserBusy(true);
    setError("");
    setNewUserResult(null);
    setPwdCopied(false);
    try {
      const { data: body } = await api.post("/api/admin/users", {
        email: userForm.email.trim().toLowerCase(),
        name: userForm.name.trim(),
        role: userForm.role,
      });
      setNewUserResult(body);
      setUserForm({ email: "", name: "", role: "consultant" });
      await refresh();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setUserBusy(false);
    }
  }

  async function copyPassword() {
    if (!newUserResult?.password) return;
    try {
      await navigator.clipboard.writeText(newUserResult.password);
      setPwdCopied(true);
      setTimeout(() => setPwdCopied(false), 2000);
    } catch (_) {
      // clipboard permission denied — fall back to manual copy
    }
  }

  // ---------- derived state for rendering
  const orgsToRender = showDeleted
    ? data.organizations
    : data.organizations.filter((o) => !o.deleted_at);
  const depsToRender = showDeleted
    ? data.deployments
    : data.deployments.filter((d) => !d.deleted_at);

  return (
    <div className="min-h-screen bg-paper" data-testid="admin-cleanup-page">
      <header className="border-b border-gray-100 sticky top-0 z-10 bg-paper">
        <div className="max-w-7xl mx-auto px-8 py-5 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <span className="wordmark text-sm">
              <span style={{ color: "#1A1A1A" }}>RESILIENT</span>{" "}
              <span style={{ color: "#2F6B7C" }}>TEAMS</span>
            </span>
          </div>
          <div className="flex items-center gap-6">
            <Link to="/dashboard" className="btn-quiet" data-testid="cleanup-back-link" style={{ textDecoration: "none" }}>
              ← Home
            </Link>
            <button onClick={logout} className="btn-quiet" data-testid="logout-button">
              Sign out
            </button>
          </div>
        </div>
      </header>

      <main id="main-content" className="max-w-7xl mx-auto px-8 py-12">
        <div className="mb-10 fade-up">
          <div className="eyebrow mb-3">Admin · Cleanup</div>
          <h1 style={{ fontSize: "clamp(2rem, 4vw, 3rem)" }} className="mb-3">
            Soft-delete &amp; restore.
          </h1>
          <p className="lead max-w-prose">
            Sweep test deployments and organizations. Soft delete sets a
            tombstone — data stays in the database and queries filter it out,
            but every action is reversible and written to the audit log.
            Deleting an org requires that every deployment underneath has
            already been deleted.
          </p>
        </div>

        {error && (
          <div className="panel mb-8" style={{ borderColor: "#AD393E" }} data-testid="cleanup-error">
            <div className="eyebrow mb-2" style={{ color: "#AD393E" }}>Something went wrong</div>
            <div className="text-sm">{error}</div>
          </div>
        )}

        {/* ============== Seed demo data ============== */}
        <section
          className="panel mb-8"
          style={{ borderColor: "#2F6B7C", background: "#FAFAFA" }}
          data-testid="seed-demo-section"
        >
          <div className="flex items-start justify-between gap-6">
            <div className="flex-1">
              <div className="eyebrow mb-2" style={{ color: "#2F6B7C" }}>Demo data &middot; one click</div>
              <h2 className="text-lg mb-2" style={{ fontFamily: "Inter", fontWeight: 500 }}>
                Seed Granite Logistics
              </h2>
              <p className="text-sm text-gray-700 max-w-prose">
                Inserts a canonical demo: 1 organization (<strong>Granite Logistics</strong>),
                1 closed deployment (<strong>Q1 2026 Pulse</strong>), 12 completed respondents
                spread across 4 teams &times; 3 roles, and 8 free-text reflections. Dimension
                profile is intentionally mixed — Purpose lands <em>Strong</em>, Social Support
                lands <em>At Risk</em>, the rest sit mid-band — so every panel has
                something meaningful to render. Idempotent; safe to click more than once.
              </p>
              {seedResult && (
                <div
                  className="mt-3 text-xs"
                  style={{ fontFamily: "Oswald", letterSpacing: "0.08em", color: "#2F6B7C" }}
                  data-testid="seed-demo-result"
                >
                  {seedResult.already_existed ? (
                    <>Already seeded &middot; deployment id <code>{seedResult.deployment_id}</code></>
                  ) : (
                    <>Seeded {seedResult.seeded_count} respondents &middot; deployment id <code>{seedResult.deployment_id}</code></>
                  )}
                  {" "}
                  <Link
                    to={`/dashboard/deployments/${seedResult.deployment_id}?tab=heatmap`}
                    style={{ color: "#2F6B7C", textDecoration: "underline" }}
                    data-testid="seed-demo-open-link"
                  >
                    Open deployment &rarr;
                  </Link>
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={doSeedDemo}
              disabled={seedBusy}
              data-testid="seed-demo-button"
              style={{
                fontFamily: "Oswald", letterSpacing: "0.18em", textTransform: "uppercase",
                fontSize: "11px", color: "#FBFAF7",
                background: seedBusy ? "#999898" : "#2F6B7C",
                border: 0, padding: "10px 18px",
                cursor: seedBusy ? "wait" : "pointer",
                whiteSpace: "nowrap",
                alignSelf: "flex-start",
              }}
            >
              {seedBusy ? "Seeding…" : "Seed demo data"}
            </button>
          </div>
        </section>

        {/* ============== Seed PRI demo data ============== */}
        <section
          className="panel mb-8"
          style={{ borderColor: "#2563EB", background: "#FAFAFA" }}
          data-testid="seed-pri-demo-section"
        >
          <div className="flex items-start justify-between gap-6">
            <div className="flex-1">
              <div className="eyebrow mb-2" style={{ color: "#2563EB" }}>PRI demo data &middot; one click</div>
              <h2 className="text-lg mb-2" style={{ fontFamily: "Inter", fontWeight: 500 }}>
                Seed three PRI coaching engagements
              </h2>
              <p className="text-sm text-gray-700 max-w-prose">
                Inserts three individual-mode coaching engagements under
                <strong> Granite Logistics</strong>: <strong>Priya Rao</strong> (high-optimism
                overextender, Social Support at-risk), <strong>Marcus Alvarez</strong>
                {" "}(mission-driven exhaustion, Emotional Regulation at-risk),
                and <strong>Jenna Chen</strong> (steady middle-band with Grit strong,
                Flexibility emerging). Each has completed the PRI survey with a
                distinct profile so the coach view, item-ranking view,
                Coach Notes highlighting, and PDF report all have three
                different report shapes to inspect side-by-side. Attached to
                the demo consultant account (<code>consultant@resilientteams.io</code>).
                Idempotent; safe to click more than once.
              </p>
              {priSeedResult && (
                <div
                  className="mt-3 text-xs"
                  style={{ fontFamily: "Oswald", letterSpacing: "0.08em", color: "#2563EB" }}
                  data-testid="seed-pri-demo-result"
                >
                  {priSeedResult.seeded.length > 0 && (
                    <div className="mb-1">
                      Seeded {priSeedResult.seeded.length} coachee(s):
                      {priSeedResult.seeded.map((s, i) => (
                        <span key={s.deployment_id}>
                          {i > 0 ? ", " : " "}
                          <Link
                            to={`/dashboard/deployments/${s.deployment_id}`}
                            style={{ color: "#2563EB", textDecoration: "underline" }}
                            data-testid={`seed-pri-demo-open-${s.deployment_id}`}
                          >
                            {s.client_name}
                          </Link>
                        </span>
                      ))}
                    </div>
                  )}
                  {priSeedResult.skipped_existing.length > 0 && (
                    <div style={{ color: "#7F7F7F" }}>
                      Already existed (skipped): {priSeedResult.skipped_existing.join(", ")}
                    </div>
                  )}
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={doSeedPriDemo}
              disabled={priSeedBusy || priResetBusy}
              data-testid="seed-pri-demo-button"
              style={{
                fontFamily: "Oswald", letterSpacing: "0.18em", textTransform: "uppercase",
                fontSize: "11px", color: "#FBFAF7",
                background: (priSeedBusy || priResetBusy) ? "#999898" : "#2563EB",
                border: 0, padding: "10px 18px",
                cursor: (priSeedBusy || priResetBusy) ? "wait" : "pointer",
                whiteSpace: "nowrap",
                alignSelf: "flex-start",
              }}
            >
              {priSeedBusy ? "Seeding…" : "Seed PRI demo"}
            </button>
          </div>
          {/* Reset row — quieter treatment than the primary Seed action */}
          <div
            className="flex items-center justify-between gap-4 mt-4 pt-4"
            style={{ borderTop: "1px solid #E0E0E0" }}
          >
            <div className="text-xs" style={{ color: "#7F7F7F", flex: 1 }}>
              Need a fresh set? Reset wipes the three demo coachees
              (deployments, respondents, item responses, reflections,
              scores, coaching plans, report snapshots) so the next
              &ldquo;Seed PRI demo&rdquo; click regenerates them clean. Audit-logged.
              Cannot touch real customer data — matches only the three
              exact demo names under Granite Logistics.
              {priResetResult && (
                <div
                  className="mt-2"
                  style={{ fontFamily: "Oswald", letterSpacing: "0.08em", color: "#7F7F7F" }}
                  data-testid="reset-pri-demo-result"
                >
                  {priResetResult.removed.deployments > 0 ? (
                    <>
                      Reset {priResetResult.removed.deployments} deployment(s):{" "}
                      {priResetResult.reset_deployments.join(", ")}
                    </>
                  ) : (
                    <>Nothing to reset — demo coachees are not currently seeded.</>
                  )}
                </div>
              )}
            </div>
            {priResetConfirming ? (
              <div className="flex items-center gap-2">
                <span className="text-xs" style={{ color: "#AD393E", fontFamily: "Oswald", letterSpacing: "0.08em" }}>
                  Confirm?
                </span>
                <button
                  type="button"
                  onClick={doResetPriDemo}
                  disabled={priResetBusy}
                  data-testid="reset-pri-demo-confirm-button"
                  style={{
                    fontFamily: "Oswald", letterSpacing: "0.14em", textTransform: "uppercase",
                    fontSize: "10px", color: "#FBFAF7",
                    background: priResetBusy ? "#999898" : "#AD393E",
                    border: 0, padding: "8px 14px",
                    cursor: priResetBusy ? "wait" : "pointer",
                  }}
                >
                  {priResetBusy ? "Resetting…" : "Yes, reset"}
                </button>
                <button
                  type="button"
                  onClick={() => setPriResetConfirming(false)}
                  disabled={priResetBusy}
                  data-testid="reset-pri-demo-cancel-button"
                  style={{
                    fontFamily: "Oswald", letterSpacing: "0.14em", textTransform: "uppercase",
                    fontSize: "10px", color: "#7F7F7F",
                    background: "transparent",
                    border: "1px solid #E0E0E0", padding: "8px 14px",
                    cursor: "pointer",
                  }}
                >
                  Cancel
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setPriResetConfirming(true)}
                disabled={priSeedBusy || priResetBusy}
                data-testid="reset-pri-demo-button"
                style={{
                  fontFamily: "Oswald", letterSpacing: "0.14em", textTransform: "uppercase",
                  fontSize: "10px", color: "#7F7F7F",
                  background: "transparent",
                  border: "1px solid #E0E0E0", padding: "8px 14px",
                  cursor: (priSeedBusy || priResetBusy) ? "wait" : "pointer",
                  whiteSpace: "nowrap",
                }}
              >
                Reset demo coachees
              </button>
            )}
          </div>
        </section>

        {/* ============== Provision new consultant ============== */}
        <section
          className="panel mb-8"
          style={{ borderColor: "#5C3A8B", background: "#FAFAFA" }}
          data-testid="provision-user-section"
        >
          <div className="eyebrow mb-2" style={{ color: "#5C3A8B" }}>Provision user</div>
          <h2 className="text-lg mb-2" style={{ fontFamily: "Inter", fontWeight: 500 }}>
            Add a new consultant
          </h2>
          <p className="text-sm text-gray-700 max-w-prose mb-4">
            Creates a new login. A strong random password is generated and
            shown <strong>once</strong> in the response below — copy it and
            hand it to the consultant over a secure channel. The password
            is bcrypt-hashed before it touches Mongo; the cleartext is never
            persisted anywhere.
          </p>
          <form
            onSubmit={doProvisionUser}
            className="grid grid-cols-1 md:grid-cols-[1fr_1fr_140px_140px] gap-3"
          >
            <input
              type="email"
              value={userForm.email}
              onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
              placeholder="consultant.email@firm.com"
              required
              data-testid="provision-user-email"
              className="field text-sm"
              style={{ padding: "10px 12px", border: "1px solid #DDDDDD", background: "white" }}
            />
            <input
              type="text"
              value={userForm.name}
              onChange={(e) => setUserForm({ ...userForm, name: e.target.value })}
              placeholder="Full name"
              required
              minLength={1}
              data-testid="provision-user-name"
              className="field text-sm"
              style={{ padding: "10px 12px", border: "1px solid #DDDDDD", background: "white" }}
            />
            <select
              value={userForm.role}
              onChange={(e) => setUserForm({ ...userForm, role: e.target.value })}
              data-testid="provision-user-role"
              style={{
                padding: "10px 12px", border: "1px solid #DDDDDD",
                background: "white", fontSize: 14, fontFamily: "Oswald", letterSpacing: "0.08em",
              }}
            >
              <option value="consultant">Consultant</option>
              <option value="super_admin">Super admin</option>
            </select>
            <button
              type="submit"
              disabled={userBusy}
              data-testid="provision-user-submit"
              style={{
                fontFamily: "Oswald", letterSpacing: "0.18em", textTransform: "uppercase",
                fontSize: "11px", color: "#FBFAF7",
                background: userBusy ? "#999898" : "#5C3A8B",
                border: 0, padding: "10px 18px",
                cursor: userBusy ? "wait" : "pointer",
              }}
            >
              {userBusy ? "Creating…" : "Create user"}
            </button>
          </form>
          {newUserResult && (
            <div
              className="mt-4 p-4"
              style={{ background: "#FFF8E8", border: "1px solid #E5C56B" }}
              data-testid="provision-user-result"
            >
              <div className="eyebrow mb-2" style={{ color: "#8B6914" }}>
                One-time password &middot; copy now
              </div>
              <div className="text-sm mb-2">
                <strong>{newUserResult.user.email}</strong> &middot; {newUserResult.user.role.replace("_", " ")}
              </div>
              <div className="flex items-center gap-2">
                <code
                  data-testid="provision-user-password"
                  style={{
                    flex: 1, padding: "10px 12px",
                    background: "white", border: "1px solid #DDDDDD",
                    fontFamily: "monospace", fontSize: "14px", letterSpacing: "0.05em",
                    userSelect: "all", wordBreak: "break-all",
                  }}
                >
                  {newUserResult.password}
                </code>
                <button
                  type="button"
                  onClick={copyPassword}
                  data-testid="provision-user-copy"
                  style={{
                    fontFamily: "Oswald", letterSpacing: "0.18em", textTransform: "uppercase",
                    fontSize: "10px", color: "#FBFAF7", background: "#1A1A1A",
                    border: 0, padding: "10px 14px", cursor: "pointer",
                    whiteSpace: "nowrap",
                  }}
                >
                  {pwdCopied ? "Copied ✓" : "Copy"}
                </button>
              </div>
              <div className="text-xs text-gray-600 mt-2" style={{ fontFamily: "Oswald", letterSpacing: "0.08em" }}>
                This password will not be shown again. Share it via a secure channel and prompt the user to rotate after first login.
              </div>
            </div>
          )}

          {/* Existing users (read-only listing) */}
          {users.length > 0 && (
            <div className="mt-5">
              <div className="eyebrow mb-2">
                Existing users &middot; {users.length}
              </div>
              <div className="text-xs" style={{ fontFamily: "Oswald", letterSpacing: "0.08em" }}>
                {users.map((u) => (
                  <div
                    key={u.id}
                    className="py-1.5"
                    style={{ borderBottom: "1px solid #EEE" }}
                    data-testid={`existing-user-${u.id}`}
                  >
                    <span style={{ fontFamily: "Inter" }}>{u.email}</span>
                    <span className="text-gray-500"> &middot; {u.role.replace("_", " ")}</span>
                    {u.name && <span className="text-gray-500"> &middot; {u.name}</span>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </section>

        <div className="flex items-center justify-between mb-6">
          <label className="flex items-center gap-2 cursor-pointer select-none" data-testid="show-deleted-toggle-label">
            <input
              type="checkbox"
              checked={showDeleted}
              onChange={(e) => setShowDeleted(e.target.checked)}
              data-testid="show-deleted-toggle"
            />
            <span className="text-sm text-gray-700">Show soft-deleted rows</span>
          </label>
          {loading && <div className="text-sm text-gray-500">Loading…</div>}
        </div>

        {/* =========================================== Deployments */}
        <section className="mb-12" data-testid="deployments-cleanup-section">
          <div className="eyebrow mb-3">
            Deployments &middot; {depsToRender.length}
          </div>
          <div className="space-y-2">
            {depsToRender.length === 0 ? (
              <div className="text-sm text-gray-500">No deployments to display.</div>
            ) : (
              depsToRender.map((d) => {
                const org = data.organizations.find((o) => o.id === d.org_id);
                const isBusy = busyKey === `dep:${d.id}`;
                return (
                  <div
                    key={d.id}
                    className="panel flex items-center justify-between"
                    style={{ opacity: d.deleted_at ? 0.65 : 1 }}
                    data-testid={`deployment-row-${d.id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="text-sm" style={{ fontFamily: "Inter", fontWeight: 500 }}>
                        {d.name}
                        {d.deleted_at && (
                          <span
                            className="ml-2 px-2 py-0.5 text-xs uppercase tracking-widest"
                            style={{
                              fontFamily: "Oswald", letterSpacing: "0.15em",
                              background: "#999898", color: "#FBFAF7",
                            }}
                            data-testid={`deployment-deleted-badge-${d.id}`}
                          >
                            Deleted
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-gray-600 mt-1" style={{ fontFamily: "Oswald", letterSpacing: "0.1em" }}>
                        {org?.name || "?"} &middot; Wave {d.wave_number} &middot;{" "}
                        {d.n_respondents} respondent{d.n_respondents === 1 ? "" : "s"} &middot;{" "}
                        Status: {d.status || "draft"}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 ml-4">
                      {d.deleted_at ? (
                        <button
                          onClick={() => setConfirming({ kind: "deployment", id: d.id, name: d.name, action: "restore", item: d })}
                          disabled={isBusy}
                          data-testid={`deployment-restore-${d.id}`}
                          style={{
                            fontFamily: "Oswald", letterSpacing: "0.18em", textTransform: "uppercase",
                            fontSize: "10px", color: "#FBFAF7", background: "#2F6B7C",
                            border: 0, padding: "8px 14px",
                            cursor: isBusy ? "wait" : "pointer",
                          }}
                        >
                          {isBusy ? "Restoring…" : "Restore"}
                        </button>
                      ) : (
                        <button
                          onClick={() => setConfirming({ kind: "deployment", id: d.id, name: d.name, action: "delete", item: d })}
                          disabled={isBusy}
                          data-testid={`deployment-delete-${d.id}`}
                          style={{
                            fontFamily: "Oswald", letterSpacing: "0.18em", textTransform: "uppercase",
                            fontSize: "10px", color: "#AD393E", background: "transparent",
                            border: "1px solid #AD393E", padding: "7px 13px",
                            cursor: isBusy ? "wait" : "pointer",
                          }}
                        >
                          {isBusy ? "Deleting…" : "Delete"}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </section>

        {/* =========================================== Organizations */}
        <section className="mb-12" data-testid="orgs-cleanup-section">
          <div className="eyebrow mb-3">
            Organizations &middot; {orgsToRender.length}
          </div>
          <div className="space-y-2">
            {orgsToRender.length === 0 ? (
              <div className="text-sm text-gray-500">No organizations to display.</div>
            ) : (
              orgsToRender.map((o) => {
                const active = activeDepsByOrg[o.id] || 0;
                const isBusy = busyKey === `org:${o.id}`;
                const blocked = !o.deleted_at && active > 0;
                return (
                  <div
                    key={o.id}
                    className="panel flex items-center justify-between"
                    style={{ opacity: o.deleted_at ? 0.65 : 1 }}
                    data-testid={`org-row-${o.id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="text-sm" style={{ fontFamily: "Inter", fontWeight: 500 }}>
                        {o.name}
                        {o.deleted_at && (
                          <span
                            className="ml-2 px-2 py-0.5 text-xs uppercase tracking-widest"
                            style={{
                              fontFamily: "Oswald", letterSpacing: "0.15em",
                              background: "#999898", color: "#FBFAF7",
                            }}
                            data-testid={`org-deleted-badge-${o.id}`}
                          >
                            Deleted
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-gray-600 mt-1" style={{ fontFamily: "Oswald", letterSpacing: "0.1em" }}>
                        {active} active deployment{active === 1 ? "" : "s"}
                        {o.primary_color && <> &middot; {o.primary_color}</>}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 ml-4">
                      {o.deleted_at ? (
                        <button
                          onClick={() => setConfirming({ kind: "org", id: o.id, name: o.name, action: "restore", item: o })}
                          disabled={isBusy}
                          data-testid={`org-restore-${o.id}`}
                          style={{
                            fontFamily: "Oswald", letterSpacing: "0.18em", textTransform: "uppercase",
                            fontSize: "10px", color: "#FBFAF7", background: "#2F6B7C",
                            border: 0, padding: "8px 14px",
                            cursor: isBusy ? "wait" : "pointer",
                          }}
                        >
                          {isBusy ? "Restoring…" : "Restore"}
                        </button>
                      ) : (
                        <button
                          onClick={() => setConfirming({ kind: "org", id: o.id, name: o.name, action: "delete", item: o })}
                          disabled={isBusy || blocked}
                          title={blocked ? `Delete the ${active} active deployment${active === 1 ? "" : "s"} first.` : ""}
                          data-testid={`org-delete-${o.id}`}
                          style={{
                            fontFamily: "Oswald", letterSpacing: "0.18em", textTransform: "uppercase",
                            fontSize: "10px",
                            color: blocked ? "#999898" : "#AD393E",
                            background: "transparent",
                            border: `1px solid ${blocked ? "#DDDDDD" : "#AD393E"}`,
                            padding: "7px 13px",
                            cursor: isBusy ? "wait" : (blocked ? "not-allowed" : "pointer"),
                          }}
                        >
                          {isBusy ? "Deleting…" : "Delete"}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </section>

        {/* =========================================== Confirm modal */}
        {confirming && (
          <ConfirmModal
            confirming={confirming}
            onCancel={() => setConfirming(null)}
            onConfirm={async () => {
              const { kind, action, item } = confirming;
              if (kind === "deployment" && action === "delete") await doDeploymentDelete(item);
              if (kind === "deployment" && action === "restore") await doDeploymentRestore(item);
              if (kind === "org" && action === "delete") await doOrgDelete(item);
              if (kind === "org" && action === "restore") await doOrgRestore(item);
            }}
          />
        )}

        <SuppressionAuditLink />
      </main>
    </div>
  );
}


/**
 * Sprint 11 Phase 2 — Link card to the dedicated suppression-bypass
 * audit viewer (lives at `/dashboard/admin/audit/suppression-bypass`).
 * This page is "cleanup" (delete / restore); the audit log is its own
 * surface so security-review work doesn't read like janitorial work.
 */
function SuppressionAuditLink() {
  return (
    <section className="mt-12" data-testid="suppression-audit-link">
      <h2 className="text-base mb-3" style={{ fontFamily: "Oswald", letterSpacing: "0.15em", textTransform: "uppercase", color: "#2563EB" }}>
        Audit
      </h2>
      <Link
        to="/dashboard/admin/audit/suppression-bypass"
        className="block p-5"
        style={{ background: "white", border: "1px solid #E0E0E0", textDecoration: "none", color: "#1A1A1A" }}
        data-testid="open-suppression-audit"
      >
        <div className="text-sm font-medium">Suppression-bypass audit log →</div>
        <div className="text-xs text-gray-600 mt-1 max-w-prose">
          Read-only review of every single-client-data access on individual deployments
          (allowed and denied). Super-admin only.
        </div>
      </Link>
    </section>
  );
}


function ConfirmModal({ confirming, onCancel, onConfirm }) {
  const { kind, action, name } = confirming;
  const isDelete = action === "delete";
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ background: "rgba(26,26,26,0.4)" }}
      onClick={onCancel}
      data-testid="cleanup-confirm-modal"
    >
      <div
        className="bg-paper p-8 w-full max-w-md"
        style={{ border: "1px solid #DDDDDD" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="eyebrow mb-2">
          {isDelete ? "Confirm delete" : "Confirm restore"}
        </div>
        <h3 className="text-xl mb-3" style={{ fontFamily: "Oswald", fontWeight: 700, color: "#1A1A1A" }}>
          {isDelete ? "Delete" : "Restore"} {kind === "deployment" ? "deployment" : "organization"}?
        </h3>
        <p className="text-sm text-gray-700 mb-5 max-w-prose">
          {isDelete ? (
            <>
              <strong>{name}</strong> will be soft-deleted. It disappears from
              views and lists, but the underlying data stays in the
              database. You can restore it from this page at any time. This
              action is written to the audit log.
            </>
          ) : (
            <>
              <strong>{name}</strong> will be restored. It will reappear in
              views and lists immediately. This action is written to the
              audit log.
            </>
          )}
        </p>
        <div className="flex items-center justify-end gap-3">
          <button
            onClick={onCancel}
            className="btn-quiet"
            data-testid="cleanup-confirm-cancel"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            data-testid="cleanup-confirm-go"
            style={{
              fontFamily: "Oswald", letterSpacing: "0.18em", textTransform: "uppercase",
              fontSize: "11px", color: "#FBFAF7",
              background: isDelete ? "#AD393E" : "#2F6B7C",
              border: 0, padding: "10px 18px",
              cursor: "pointer",
            }}
          >
            {isDelete ? "Yes, delete" : "Yes, restore"}
          </button>
        </div>
      </div>
    </div>
  );
}
